import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Alert, AlertAction } from '@simplywallst/ui-core';

import { useSnackbar } from '@/features/snackbar';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';

import { LinkStatusWrapper } from './styled';

const ReconnectPortfolioProviderLinkUrl = loadable(
  () => import('../PortfolioLinking'),
  { resolveComponent: (c) => c.ReconnectPortfolioProviderLinkUrl }
);

const ReconnectPortfolioProviderLinkAccessToken = loadable(
  () => import('../PortfolioLinking'),
  { resolveComponent: (c) => c.ReconnectPortfolioProviderLinkAccessToken }
);

interface Props {
  portfolio: Portfolio;
}

const DisconnectedState = ({ portfolio }: Props) => {
  const { addSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const { Track, trackEvent } = useTracking({
    modifier: 'reconnectWithBrokerage',
  });

  const handleClick = useCallback(
    (portfolioId: Portfolio['id']) => {
      setShowModal(true);
      trackEvent({ action: 'click', portfolioId });
    },
    [trackEvent]
  );

  const handleClose = useCallback(() => {
    setShowModal(false);
    trackEvent({ action: 'closed' });
  }, [trackEvent]);

  const handleSuccess = useCallback(() => {
    setShowModal(false);
    trackEvent({ action: 'complete' });
    queryClient.invalidateQueries({
      queryKey: [PORTFOLIOS_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [PORTFOLIO_QUERY_KEY, portfolio.id],
    });
    addSnackbar({
      type: 'positive',
      lifeTime: 2000,
      message: 'Portfolio successfully reconnected with your brokerage',
    });
  }, [queryClient, trackEvent, addSnackbar, portfolio.id]);

  const handleError = useCallback(() => {
    trackEvent({ action: 'error' });
    setShowModal(false);
    addSnackbar({
      type: 'negative',
      lifeTime: 3000,
      message: 'Failed to reconnect your portfolio with your brokerage',
    });
  }, [trackEvent, addSnackbar]);

  return (
    <Track>
      <LinkStatusWrapper>
        <Alert
          data-cy-id="alert-disconnected-message"
          styleType="warning"
          layout="stack"
          actions={
            <AlertAction
              shouldDismissAlert={false}
              onClick={() => handleClick(portfolio.id)}
            >
              Reconnect
            </AlertAction>
          }
        >
          Re-authentication required. Please update connection to retrieve
          latest data from your brokerage.
        </Alert>
      </LinkStatusWrapper>

      {showModal && portfolio.provider.name === 'yodlee' ? (
        <ReconnectPortfolioProviderLinkAccessToken
          portfolioId={portfolio.id}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={handleClose}
        />
      ) : null}

      {showModal && portfolio.provider.name === 'passiv' ? (
        <ReconnectPortfolioProviderLinkUrl
          portfolioId={portfolio.id}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={handleClose}
        />
      ) : null}

      {showModal && portfolio.provider.name === 'sharesight' ? (
        <ReconnectPortfolioProviderLinkUrl
          portfolioId={portfolio.id}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={handleClose}
        />
      ) : null}
    </Track>
  );
};

export default DisconnectedState;
